import { createElement, useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import Cookies from '@comeon/tomcat-cookies';

import { selectIsOntarioRegulatedFranchise } from '~/components/brand/ducks';
import { selectFranchiseCode, selectProposedLocale, selectLocales } from '~/components/brand/ducks/selectors';
import ButtonWrapper from '~/components/misc/button-wrapper';
import Loading from '~/components/misc/loading';
import useTranslations from '~/components/translation/use-translations';

import useConfiguration from '~/configuration/use-configuration';
import { FLAGS, proposeTextKeys } from './constants';
import { changeLanguage } from './utils';
import { insertTrackEvent } from '@comeon/analytics';
import { locale } from '~/store/cookies';

const Flag = styled.dt`
	svg {
		margin: auto;
		border-radius: 3px;
	}
`;

const ProposeNav = styled.nav`
	display: flex;
	flex-direction: column;
	justify-content: center;
`;

const ButtonWrapperLocaleField = styled(ButtonWrapper)`
	@media (min-width: 768px) {
		max-width: 500px;
	}
	width: 85%;
	margin: auto;
	padding: 16px 0 0;
	overflow: hidden;
	background-color: rgba(var(--propose-locale-head-bg, var(--base-10)), 1);
`;

const ButtonLocale = styled.button`
	margin: 0 !important;
	border-radius: 0 !important;
	background-color: rgba(var(--propose-locale-head-bg, var(--base-10)), 1) !important;
	border-top: 1px solid rgba(var(--propose-locale-border, var(--base-07)), 1);
	border-bottom: 1px solid rgba(var(--propose-locale-border, var(--base-07)), 1);
`;

const LocaleText = styled.p`
	color: var(--propose-locale-text, rgba(var(--base-02), 1));
	margin: 10px 40px;
`;

const ProposeLocale = () => {
	const [isOpen, setOpen] = useState(true);

	const isOntarioRegulatedFranchise = useSelector(selectIsOntarioRegulatedFranchise);
	const proposedLocale = useSelector(selectProposedLocale);
	const locales = useSelector(selectLocales);
	const franchiseCode = useSelector(selectFranchiseCode);

	const { translations: t, isLoadingTranslations } = useTranslations(proposeTextKeys);
	const {
		geoIp: { city, countryName, regionName, timeZone }
	} = useConfiguration();

	const proposal = locales.find(l => l.locale === proposedLocale);
	const localeCookie = locale();
	const currentLocale = locales.find(l => l.locale === localeCookie);
	const localeDescription = proposal?.description;

	const triggerTrackEvent = useCallback(
		actionValue => {
			insertTrackEvent({
				category: 'Proposed_Locale',
				action: actionValue,
				label: `current: ${localeCookie} - proposed: ${proposedLocale}`,
				pixel_country: countryName,
				pixel_language: currentLocale.description,
				pixel_franchiseName: franchiseCode,
				pixel_city: city,
				pixel_region: regionName,
				pixel_timeZone_location: timeZone
			});
		},
		[
			city,
			countryName,
			currentLocale.description,
			franchiseCode,
			localeCookie,
			proposedLocale,
			regionName,
			timeZone
		]
	);

	const closeSupport = useCallback(() => {
		triggerTrackEvent('Popup_Click_Cancel');
		Cookies.set('acceptedLocale', Cookies.get('locale'));
		setOpen(false);
	}, [triggerTrackEvent]);

	const switchLanguage = useCallback(() => {
		triggerTrackEvent('Popup_Click_OK');
		changeLanguage({ locale: proposedLocale });
	}, [proposedLocale, triggerTrackEvent]);

	useEffect(() => {
		triggerTrackEvent('Popup_Shown');
	}, []); //eslint-disable-line

	if (!countryName || !FLAGS[proposedLocale]) {
		return null;
	}

	if (isLoadingTranslations) {
		return <Loading />;
	}

	return (
		<ProposeNav
			className={`support-nav propose-locale-nav ${isOpen ? 'support-nav--is-open' : 'support-nav--is-close'}`}
			data-at="proposed-locale-popup"
		>
			<ButtonWrapperLocaleField
				smallerPositionValue="unset"
				biggerPositionValue="unset"
				className="propose-locale-wrapper"
			>
				{!isOntarioRegulatedFranchise && <Flag>{createElement(FLAGS[proposedLocale])}</Flag>}
				<div className="support-nav__head u-text-c u-pt1 u-pb1">
					<h2 className="support-nav__main-title">{localeDescription || countryName}</h2>
					<LocaleText>
						{t.SWITCH_LANGUAGE_TEXT.replace(
							'XX',
							(isOntarioRegulatedFranchise ? regionName : countryName) || localeDescription
						)}
					</LocaleText>
				</div>
				<ButtonLocale
					type="button"
					className="btn btn--secondary js-fun-mode"
					onClick={switchLanguage}
					data-at="switch-language"
				>
					{t.SWITCH_LANGUAGE_BUTTON}
				</ButtonLocale>
				<ButtonLocale
					type="button"
					className="btn btn--secondary js-close-support-nav"
					onClick={closeSupport}
					data-at="close-button"
				>
					{t.SWITCH_LANGUAGE_CANCEL}
				</ButtonLocale>
			</ButtonWrapperLocaleField>
		</ProposeNav>
	);
};

export default ProposeLocale;
